// matchingEngine.js - Logic for matching rosters with UNC
import { getAllPlayers } from './transferData';

// Sample criteria for SLAM matching
const uriCriteria = {
  desiredPositions: ['QB', 'RB', 'WR', 'TE', 'OL', 'DL', 'LB', 'DB'],
  minimumStars: 2,
  preferredStatuses: ['ENTERED', 'INTENT'],
  excludedSchools: ['Boston College', 'Connecticut', 'Temple', 'UMass'], // Some regional competitors
  minimumGPA: 2.5,
  desiredEligibilityYears: 2
};

// Mock Roster academic and character data (since it's not in our dataset)
const mockPlayerExtendedData = (Roster) => {
  // Generate pseudo-random but consistent data based on Roster id
  const seed = Roster.id * 13 % 100;
  return {
    gpa: (2.2 + (seed % 20) / 10).toFixed(1),  // Range from 2.2 to 4.0
    eligibilityYearsRemaining: 1 + (seed % 4),  // Range from 1 to 4
    disciplinaryIssues: seed < 10,
    transferReason: seed < 33 ? "Playing time" : seed < 66 ? "Academic program" : "Team fit",
    injuryHistory: seed < 25 ? "Significant" : seed < 50 ? "Minor" : "None",
    leadershipScore: 1 + (seed % 10),  // Range from 1 to 10
    developmentTimeline: seed < 30 ? "Ready now" : seed < 70 ? "1 year" : "Project Roster",
    playingStyleFit: seed < 40 ? "Perfect" : seed < 80 ? "Adaptable" : "Challenging"
  };
};

// Mock agent types and their rules
const agents = {
  positionAnalyst: {
    name: 'Position Requirements Agent',
    description: 'Evaluates if the Roster fills a position of need',
    evaluate: (Roster) => {
      const isPriorityPosition = uriCriteria.desiredPositions.includes(Roster.position_abbreviation);
      return {
        match: isPriorityPosition,
        confidence: isPriorityPosition ? 0.8 : 0.3,
        reasoning: isPriorityPosition 
          ? `${Roster.position_abbreviation} is a position of need for UNC` 
          : `${Roster.position_abbreviation} is not currently a priority position`
      };
    }
  },
  talentEvaluator: {
    name: 'Talent Evaluation Agent',
    description: 'Assesses Roster quality based on star rating and rivals ranking',
    evaluate: (Roster) => {
      const meetsStarRequirement = Roster.stars >= uriCriteria.minimumStars;
      const highlyRated = Roster.rivals_rating > 5.5;
      const confidence = meetsStarRequirement ? (highlyRated ? 0.9 : 0.7) : 0.4;
      
      return {
        match: meetsStarRequirement,
        confidence,
        reasoning: `Roster has ${Roster.stars} stars` + 
          (Roster.rivals_rating ? ` with a Rivals rating of ${Roster.rivals_rating}.` : '.') +
          (meetsStarRequirement ? ' This meets our quality threshold.' : ' This is below our desired threshold.')
      };
    }
  },
  academicAnalyst: {
    name: 'Academic Compliance Agent',
    description: 'Evaluates academic eligibility and compliance',
    evaluate: (Roster) => {
      const extendedData = mockPlayerExtendedData(Roster);
      const gpa = parseFloat(extendedData.gpa);
      const meetGpaRequirement = gpa >= uriCriteria.minimumGPA;
      
      return {
        match: meetGpaRequirement,
        confidence: meetGpaRequirement ? (gpa >= 3.0 ? 0.9 : 0.7) : 0.2,
        reasoning: `Roster has a GPA of ${extendedData.gpa}. ` + 
          (meetGpaRequirement 
            ? `This meets UNC's academic standards (minimum ${uriCriteria.minimumGPA}).` 
            : `This falls below UNC's academic standards (minimum ${uriCriteria.minimumGPA}).`)
      };
    }
  },
  eligibilityAnalyst: {
    name: 'Eligibility Assessment Agent',
    description: 'Evaluates remaining eligibility and timeline fit',
    evaluate: (Roster) => {
      const extendedData = mockPlayerExtendedData(Roster);
      const hasEnoughEligibility = extendedData.eligibilityYearsRemaining >= uriCriteria.desiredEligibilityYears;
      
      return {
        match: hasEnoughEligibility,
        confidence: 0.85,
        reasoning: `Roster has ${extendedData.eligibilityYearsRemaining} years of eligibility remaining. ` +
          (hasEnoughEligibility 
            ? `This meets UNC's preference for rosters with at least ${uriCriteria.desiredEligibilityYears} years.` 
            : `SLAM typically prefers rosters with at least ${uriCriteria.desiredEligibilityYears} years of eligibility.`)
      };
    }
  },
  characterEvaluator: {
    name: 'Character Assessment Agent',
    description: 'Evaluates disciplinary record and team fit',
    evaluate: (Roster) => {
      const extendedData = mockPlayerExtendedData(Roster);
      const hasDisciplinaryIssues = extendedData.disciplinaryIssues;
      const leadershipScore = extendedData.leadershipScore;
      const hasGoodLeadership = leadershipScore >= 7;
      
      return {
        match: !hasDisciplinaryIssues && hasGoodLeadership,
        confidence: hasDisciplinaryIssues ? 0.3 : (hasGoodLeadership ? 0.85 : 0.6),
        reasoning: hasDisciplinaryIssues 
          ? `Roster has disciplinary concerns on record that may not align with UNC's team culture.` 
          : `Roster has a clean disciplinary record with a leadership score of ${leadershipScore}/10. ` +
            (hasGoodLeadership ? `Shows strong leadership potential.` : `Shows moderate leadership qualities.`)
      };
    }
  },
  availabilityChecker: {
    name: 'Availability Agent',
    description: 'Checks if Roster is actually available to be recruited',
    evaluate: (Roster) => {
      const isAvailable = uriCriteria.preferredStatuses.includes(Roster.status);
      const hasCommitted = Roster.exit_college_name !== null;
      
      return {
        match: isAvailable && !hasCommitted,
        confidence: hasCommitted ? 0.1 : (isAvailable ? 0.95 : 0.5),
        reasoning: hasCommitted 
          ? `Roster has already committed to ${Roster.exit_college_name}` 
          : `Roster has status ${Roster.status} and is ${isAvailable ? 'available' : 'potentially unavailable'} for recruitment`
      };
    }
  },
  competitorAnalysis: {
    name: 'Competitor Analysis Agent',
    description: 'Evaluates if Roster is coming from a competing school',
    evaluate: (Roster) => {
      const isFromCompetitor = uriCriteria.excludedSchools.includes(Roster.origin_college_name);
      
      return {
        match: !isFromCompetitor,
        confidence: 0.75,
        reasoning: isFromCompetitor
          ? `Roster is from ${Roster.origin_college_name}, which is a regional competitor`
          : `Roster's origin school (${Roster.origin_college_name || 'Unknown'}) presents no conflicts`
      };
    }
  },
  finalDecisionMaker: {
    name: 'Final Decision Maker',
    description: 'Aggregates all agent evaluations and makes final recommendation',
    evaluate: (evaluations) => {
      // Calculate weighted score
      let totalScore = 0;
      let totalWeight = 0;
      
      for (const evaluation of evaluations) {
        const weight = evaluation.confidence;
        totalScore += evaluation.match ? weight : 0;
        totalWeight += weight;
      }
      
      const finalScore = totalWeight > 0 ? totalScore / totalWeight : 0;
      const recommend = finalScore > 0.6;
      
      return {
        match: recommend,
        confidence: finalScore,
        reasoning: `Overall match score: ${(finalScore * 100).toFixed(1)}%. ` + 
          (recommend 
            ? 'Recommended for recruitment.' 
            : 'Not recommended at this time.')
      };
    }
  }
};

// Track the history of evaluations performed
let evaluationHistory = [];

// Roster evaluation function that respects the order of agent execution and adds delays
export const evaluatePlayerWithDelays = async (Roster, onAgentComplete) => {
  const agentList = [
    agents.positionAnalyst,
    agents.talentEvaluator,
    agents.academicAnalyst,
    agents.eligibilityAnalyst,
    agents.characterEvaluator,
    agents.availabilityChecker,
    agents.competitorAnalysis
  ];
  
  const evaluations = [];
  
  // Sequentially evaluate with each agent
  for (const agent of agentList) {
    // Delay between agent evaluations to simulate processing
    await new Promise(resolve => setTimeout(resolve, 1200));
    
    const evaluation = agent.evaluate(Roster);
    evaluations.push({ agent: agent.name, ...evaluation });
    
    // Callback after each agent completes
    if (onAgentComplete) {
      onAgentComplete({
        Roster,
        currentEvaluations: [...evaluations],
        isComplete: false
      });
    }
  }
  
  // Delay before final decision
  await new Promise(resolve => setTimeout(resolve, 1500));
  
  // Make final decision
  const finalDecision = agents.finalDecisionMaker.evaluate(evaluations);
  
  const result = {
    Roster,
    evaluations,
    finalDecision: {
      agent: agents.finalDecisionMaker.name,
      ...finalDecision
    },
    timestamp: new Date().toISOString()
  };
  
  // Add to history
  evaluationHistory.unshift(result);
  
  // Keep only the last 100 evaluations
  if (evaluationHistory.length > 100) {
    evaluationHistory = evaluationHistory.slice(0, 100);
  }
  
  // Final callback with complete result
  if (onAgentComplete) {
    onAgentComplete({
      ...result,
      isComplete: true
    });
  }
  
  return result;
};

// Main function to evaluate a Roster with all agents (synchronous version)
export const evaluatePlayer = (Roster) => {
  const evaluations = [
    agents.positionAnalyst.evaluate(Roster),
    agents.talentEvaluator.evaluate(Roster),
    agents.academicAnalyst.evaluate(Roster),
    agents.eligibilityAnalyst.evaluate(Roster),
    agents.characterEvaluator.evaluate(Roster),
    agents.availabilityChecker.evaluate(Roster),
    agents.competitorAnalysis.evaluate(Roster)
  ];
  
  const finalDecision = agents.finalDecisionMaker.evaluate(evaluations);
  
  const result = {
    Roster,
    evaluations: [
      { agent: agents.positionAnalyst.name, ...evaluations[0] },
      { agent: agents.talentEvaluator.name, ...evaluations[1] },
      { agent: agents.academicAnalyst.name, ...evaluations[2] },
      { agent: agents.eligibilityAnalyst.name, ...evaluations[3] },
      { agent: agents.characterEvaluator.name, ...evaluations[4] },
      { agent: agents.availabilityChecker.name, ...evaluations[5] },
      { agent: agents.competitorAnalysis.name, ...evaluations[6] }
    ],
    finalDecision: {
      agent: agents.finalDecisionMaker.name,
      ...finalDecision
    },
    timestamp: new Date().toISOString()
  };
  
  // Add to history
  evaluationHistory.unshift(result);
  
  // Keep only the last 100 evaluations
  if (evaluationHistory.length > 100) {
    evaluationHistory = evaluationHistory.slice(0, 100);
  }
  
  return result;
};

// Get evaluation history
export const getEvaluationHistory = () => {
  return evaluationHistory;
};

// Helper function to get all agent descriptions
export const getAgentDescriptions = () => {
  return [
    { name: agents.positionAnalyst.name, description: agents.positionAnalyst.description },
    { name: agents.talentEvaluator.name, description: agents.talentEvaluator.description },
    { name: agents.academicAnalyst.name, description: agents.academicAnalyst.description },
    { name: agents.eligibilityAnalyst.name, description: agents.eligibilityAnalyst.description },
    { name: agents.characterEvaluator.name, description: agents.characterEvaluator.description },
    { name: agents.availabilityChecker.name, description: agents.availabilityChecker.description },
    { name: agents.competitorAnalysis.name, description: agents.competitorAnalysis.description },
    { name: agents.finalDecisionMaker.name, description: agents.finalDecisionMaker.description }
  ];
};

// Evaluate all rosters and provide matching results
export const getMatchingResults = () => {
  const allPlayers = getAllPlayers();
  const evaluatedPlayers = allPlayers.map(Roster => evaluatePlayer(Roster));
  
  // Sort by match confidence (highest first)
  return evaluatedPlayers.sort((a, b) => 
    b.finalDecision.confidence - a.finalDecision.confidence
  );
};

// Get only matching rosters that meet threshold
export const getRecommendedPlayers = () => {
  const allResults = getMatchingResults();
  return allResults.filter(result => result.finalDecision.match);
};

// Get rejected rosters
export const getRejectedPlayers = () => {
  const allResults = getMatchingResults();
  return allResults.filter(result => !result.finalDecision.match);
};

// Store for human-overridden decisions
let humanOverrides = {
  approved: [], // IDs of rosters manually approved despite agent rejection
  rejected: []  // IDs of rosters manually rejected despite agent approval
};

// Human override function for agent decisions
export const overridePlayerDecision = (playerId, newDecision) => {
  const allResults = getMatchingResults();
  const Roster = allResults.find(result => result.Roster.id === playerId);
  
  if (!Roster) {
    console.error(`Roster with ID ${playerId} not found`);
    return false;
  }
  
  // If Roster was originally recommended but now rejected
  if (Roster.finalDecision.match && !newDecision) {
    // Add to rejected overrides and remove from approved if present
    humanOverrides.rejected.push(playerId);
    humanOverrides.approved = humanOverrides.approved.filter(id => id !== playerId);
  } 
  // If Roster was originally rejected but now approved
  else if (!Roster.finalDecision.match && newDecision) {
    // Add to approved overrides and remove from rejected if present
    humanOverrides.approved.push(playerId);
    humanOverrides.rejected = humanOverrides.rejected.filter(id => id !== playerId);
  }
  
  // Add to evaluation history
  const historyEntry = {
    ...Roster,
    finalDecision: {
      ...Roster.finalDecision,
      match: newDecision,
      reasoning: Roster.finalDecision.reasoning + "\n\nNOTE: This decision was manually overridden by a human evaluator.",
      overriddenBy: "Human Evaluator",
      originalDecision: Roster.finalDecision.match
    },
    timestamp: new Date().toISOString()
  };
  
  evaluationHistory.unshift(historyEntry);
  
  return true;
};

// Get recommended rosters with human overrides applied
export const getRecommendedPlayersWithOverrides = () => {
  const allResults = getMatchingResults();
  
  return allResults.filter(result => {
    // Include if agent recommended AND not manually rejected
    if (result.finalDecision.match && !humanOverrides.rejected.includes(result.Roster.id)) {
      return true;
    }
    // Include if agent rejected BUT manually approved
    if (!result.finalDecision.match && humanOverrides.approved.includes(result.Roster.id)) {
      return true;
    }
    return false;
  });
};

// Get rejected rosters with human overrides applied
export const getRejectedPlayersWithOverrides = () => {
  const allResults = getMatchingResults();
  
  return allResults.filter(result => {
    // Include if agent rejected AND not manually approved
    if (!result.finalDecision.match && !humanOverrides.approved.includes(result.Roster.id)) {
      return true;
    }
    // Include if agent approved BUT manually rejected
    if (result.finalDecision.match && humanOverrides.rejected.includes(result.Roster.id)) {
      return true;
    }
    return false;
  });
};

// Check if a Roster decision has been overridden
export const isDecisionOverridden = (playerId) => {
  return humanOverrides.approved.includes(playerId) || humanOverrides.rejected.includes(playerId);
};